<template>
  <el-dialog v-model="isVisible" :close-on-click-modal="true" :show-close="false" width="600px" class="design-dialog">
    <template #title>
      <div class="dialog-header">
        <svg-icon name="close" class="close-icon" @click="closeDialog" />
        <div class="header-title">Header design</div>
        <el-button type="primary" @click="publishDesign" :disabled="noPublish">Publish</el-button>
      </div>
    </template>

    <div class="dialog-body">
      <div class="form-row form-row-inline">
        <label class="form-label">Title<span class="req">*</span></label>
        <el-input v-model="form.title" maxlength="50" show-word-limit placeholder="Enter title (max 50 chars)" />
      </div>

      <div class="form-row form-row2">
        <label class="form-label">Text color</label>
        <div class="color-row" @click="openColorPicker">
          <span class="color-wrap">
            <ColorPicker v-model:pureColor="form.textColor" :disableAlpha="true" />
          </span>
          <div class="color-code">#{{ getColorHex(form.textColor) }}</div>
        </div>
      </div>

      <div class="form-row form-row-inline">
        <label class="form-label">Sub-text<span class="req">*</span></label>
        <el-input
          type="textarea"
          v-model="form.subtext"
          maxlength="100"
          show-word-limit
          :rows="3"
          placeholder="Enter sub-text (max 100 chars)"
        />
      </div>

      <div class="form-row form-row2">
        <label class="form-label">Sub-text color</label>
        <div class="color-row" @click="openColorPicker">
          <span class="color-wrap">
            <ColorPicker v-model:pureColor="form.subtextColor" :disableAlpha="true" />
          </span>
          <div class="color-code">#{{ getColorHex(form.subtextColor) }}</div>
        </div>
      </div>

      <div class="form-row form-row2">
        <label class="form-label">Background</label>
        <div class="bg-options">
          <el-button-group>
            <el-button
              :type="form.backgroundType === 'color' ? 'primary' : 'default'"
              @click="form.backgroundType = 'color'"
            >
              <svg-icon name="color-pick" />
            </el-button>
            <el-button
              :type="form.backgroundType === 'image' ? 'primary' : 'default'"
              @click="form.backgroundType = 'image'"
            >
              <svg-icon name="image" />
            </el-button>
          </el-button-group>
        </div>
      </div>

      <div v-if="form.backgroundType === 'color'" class="form-row form-row2">
        <label class="form-label">Background color</label>
        <div class="color-row" @click="openColorPicker">
          <span class="color-wrap">
            <ColorPicker v-model:pureColor="form.color" :disableAlpha="true" />
          </span>
          <div class="color-code">#{{ getColorHex(form.color) }}</div>
        </div>
      </div>

      <div v-else class="form-row form-row-inline">
        <label class="form-label">Image</label>
        <div class="image-uploader">
          <div class="upload-hint">Please upload a PNG, JPG or JPEG image with 4:1 aspect ratio. File size not over 10MB</div>
          <div class="upload-area" @click="openUploadDialog">
            <template v-if="form.imageUrl">
              <img :src="form.imageUrl" alt="preview" />
            </template>
            <template v-else>
              <div class="upload-btn">Upload image</div>
            </template>
          </div>

          <el-dialog
            v-model="uploadDialogVisible"
            width="800px"
            :append-to-body="true"
            :destroy-on-close="true"
            class="upload-image-dialog"
          >
            <template #title>
              <div class="dialog-header">Upload Image</div>
            </template>
            <div class="dialog-body">
              <UploadImage
                v-if="uploadDialogVisible"
                :aspectRatio="4"
                :maxImage="10"
                :acceptType="'image/png, image/jpg, image/jpeg'"
                @saveAvatar="onUploadSavedAndClose"
              />
            </div>
          </el-dialog>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import { computed, ref, watch, getCurrentInstance } from 'vue';
import { ElInput, ElButton, ElDialog, ElMessage, ElButtonGroup } from 'element-plus';
import { ColorPicker } from 'vue3-colorpicker';
import 'vue3-colorpicker/style.css';
import UploadImage from '@/components/ui/UploadImage.vue';
import { useNotification } from '@/components/ui/notification';

const { openNotBottom } = useNotification();
const { $ProjectName } = getCurrentInstance().appContext.config.globalProperties;

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  initialForm: {
    type: Object,
    default: () => ({
      title: 'Creator Academy',
      subtext: 'Manage your Creator Academy content here.',
      backgroundType: 'color',
      color: '#F1F4F9',
      textColor: '#000000',
      subtextColor: '#000000',
      image: null,
      imageUrl: '',
    }),
  },
});

const emit = defineEmits(['update:visible', 'publish']);

const isVisible = ref(props.visible);
const uploadDialogVisible = ref(false);

const defaultTitle = $ProjectName === 'cj' ? 'Creator Academy' : 'Creator HQ';
const defaultSubtext = $ProjectName === 'cj' ? 'Manage your Creator Academy content here.' : 'Manage your Creator HQ content here.';

const form = ref({
  title: props.initialForm.title || defaultTitle,
  subtext: props.initialForm.subtext || defaultSubtext,
  backgroundType: props.initialForm.backgroundType || 'color',
  color: props.initialForm.color || '#F1F4F9',
  textColor: props.initialForm.textColor || '#000000',
  subtextColor: props.initialForm.subtextColor || '#000000',
  image: props.initialForm.image || null,
  imageUrl: props.initialForm.imageUrl || '',
});

// snapshot to track initial form state for reset on close
const initialFormSnapshot = ref(null);

// sync with parent -
watch(
  () => props.visible,
  (newVal) => {
    isVisible.value = newVal;
    // open dilaog and set form values
    if (newVal) {
      form.value = {
        title: props.initialForm.title || defaultTitle,
        subtext: props.initialForm.subtext || defaultSubtext,
        backgroundType: props.initialForm.backgroundType || 'color',
        color: props.initialForm.color || '#F1F4F9',
        textColor: props.initialForm.textColor || '#000000',
        subtextColor: props.initialForm.subtextColor || '#000000',
        image: props.initialForm.image || null,
        imageUrl: props.initialForm.imageUrl || '',
      };
      initialFormSnapshot.value = JSON.parse(JSON.stringify(form.value));
    }
  }
);

watch(
  () => isVisible.value,
  (newVal) => {
    emit('update:visible', newVal);
    if (!newVal && initialFormSnapshot.value) {
      form.value = JSON.parse(JSON.stringify(initialFormSnapshot.value));
    }
  }
);

// emit form changes for live preview
watch(
  () => ({ ...form.value }),
  (newForm) => {
    emit('form-change', newForm);
  }
);

const noPublish = computed(() => {
  if (!form.value.title || !form.value.subtext || (form.value.backgroundType==='image' && !form.value.imageUrl && !form.value.image)) {
    return true;
  }
  return false;
});
//++++++++++++++++++++++++++++++++++++++
const getColorHex = (color) => {
  if (!color) return '';
  const c = color.trim();
  if (c.startsWith('#')) {
    const hex = c.replace('#', '');
    const full =
      hex.length === 3
        ? hex
            .split('')
            .map((x) => x + x)
            .join('')
        : hex;
    return full.toUpperCase();
  }
  const m = c.match(/rgba?\(([^)]+)\)/i);
  if (m) {
    const parts = m[1].split(',').map((p) => parseInt(p, 10));
    const hex = parts
      .slice(0, 3)
      .map((n) => (Number.isNaN(n) ? '00' : n.toString(16).padStart(2, '0')))
      .join('')
      .toUpperCase();
    return hex;
  }
  return '';
};

const openColorPicker = (e) => {
  const row = e.currentTarget;
  const trigger = row.querySelector('.current-color');
  if (trigger && !trigger.contains(e.target)) {
    trigger.click();
  }
};

const closeDialog = () => {
  isVisible.value = false;
};

const openUploadDialog = () => {
  uploadDialogVisible.value = true;
};

const onUploadSavedAndClose = (imgData) => {
  // Validate image format
  const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
  if (!allowedTypes.includes(imgData.type)) {
    openNotBottom('Only PNG, JPG or JPEG images are allowed.', 'error');
    return;
  }
  form.value.image = imgData.data;
  form.value.imageUrl = imgData.src;
  uploadDialogVisible.value = false;
};

const publishDesign = () => {
  // validation
  if (!form.value.title || !form.value.subtext) {
    openNotBottom('Title and Sub-text are required.', 'error');
    return;
  }
  if (form.value.title.length > 50) {
    openNotBottom('Title must be 50 characters or less.', 'error');
    return;
  }
  if (form.value.subtext.length > 100) {
    openNotBottom('Sub-text must be 100 characters or less.', 'error');
    return;
  }
  if (form.value.backgroundType === 'image' && (!form.value.image && !form.value.imageUrl)) {
    openNotBottom('Please select an image for the background.', 'error');
    return;
  }

  // Ensure colors are in #XXXXXX format
  const publishForm = { ...form.value };
  publishForm.color = '#' + getColorHex(form.value.color);
  publishForm.textColor = '#' + getColorHex(form.value.textColor);
  publishForm.subtextColor = '#' + getColorHex(form.value.subtextColor);

  // clear snapshot after publish
  initialFormSnapshot.value = null;
  emit('publish', publishForm);
  isVisible.value = false;
};
</script>

<style lang="scss">
.design-dialog {
  .dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 16px;

    .close-icon {
      cursor: pointer;
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }

    .header-title {
      flex: 1;
      text-align: left;
      font: var(--font-med-24);
    }
  }

  .dialog-body {
    padding: 8px 0 16px;
  }

  .form-row {
    display: flex;
    align-items: flex-start;
    margin-bottom: 16px;
  }

  .form-row-inline {
    flex-direction: column;
  }

  .form-label {
    width: auto;
    margin-bottom: 8px;
    font: var(--font-med-16);

    .req {
      color: var(--red);
    }
  }

  .form-row2 {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .bg-options {
      svg {
        width: 14px;
        height: 14px;
      }
      .el-button--primary {
        svg {
          fill: #fff;
        }
      }
    }
  }

  .el-button-group {
    display: flex;
    gap: 0;
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
}

.color-row {
  display: flex;
  align-items: center;
  gap: 0;
  width: fit-content;
  cursor: pointer;

  border: 1px solid var(--black-20);
  border-radius: 4px;
  background-color: #fff;
  height: 32px;
  padding: 0 8px 0 4px;
}

.color-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-right: 8px;
  overflow: hidden;
  cursor: pointer;

  .vc-color-wrap,
  .vc-color,
  .el-color-picker__trigger {
    width: 24px;
    height: 24px;
    border: none;
    padding: 0;
    margin: 0;
  }
  .vc-color-wrap {
    min-width: 24px;
    margin: 0;
  }
}

.color-code {
  font-size: 12px;
}

.image-uploader {
  width: 100%;

  .upload-hint {
    color: var(--color-danger);
    font-size: 12px;
    margin-bottom: 8px;
  }

  .upload-area {
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 1;
    border-radius: 4px;
    cursor: pointer;
    overflow: hidden;

    background-color: #f5f5f5;
    background-image: linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
      linear-gradient(-45deg, #e0e0e0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
      linear-gradient(-45deg, transparent 75%, #e0e0e0 75%);
    background-size: 20px 20px;
    background-position: 0 0, 0 10px, 10px -10px, -10px 0px;

    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      outline: 1px solid var(--black-30);
    }

    input.file-input {
      display: none;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .upload-btn {
      background-color: #fff;
      border: 1px solid var(--black-20);
      border-radius: 4px;
      padding: 6px 14px;
      font-size: 12px;
      cursor: pointer;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

      &:hover {
        border-color: var(--black-40);
      }
    }
  }
}
</style>
